import React, { useEffect, useMemo, useState } from 'react';
import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux';

import { TSegmentedBannerAlias } from '@r1-frontend/api-domru/spec-offer/v1/banners/dto/responseDto';
import { ISpecOffer } from '@r1-frontend/api-domru/spec-offer/v1/spec-offers';

import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { useMatchMediaByWidth } from '@r1-frontend/shared/hooks/useMatchMedia';

import { specialOfferAnalytics } from '~/src/features/specialOffersContainer/analytics';
import { DefaultCallback } from '~/src/features/specialOffersContainer/constants';
import LkPromotionalCard from '~/src/features/specialOffersContainer/containers/LkPromotionalCard';
import LkPromotionalPopup from '~/src/features/specialOffersContainer/containers/LkPromotionalPopup';
import {
    PromoConnectedPopup,
    PromoConnectionPopup,
    PromoErrorPopup,
    PromoNeedPaymentPopup,
    PromoNeedSetPhonePopup,
} from '~/src/features/specialOffersContainer/containers/popups';
import SpecOfferDetails from '~/src/features/specialOffersContainer/containers/SpecOfferDetails';
import { getGaLabelByNavigationType, isNewDetailsDesign } from '~/src/features/specialOffersContainer/helpers';
import { useCallbackMap } from '~/src/features/specialOffersContainer/hooks';
import { setIsPopupOpened, setPopupState } from '~/src/features/specialOffersContainer/store/actions';
import { selectOffer } from '~/src/features/specialOffersContainer/store/selectors';
import { TSpecOffer } from '~/src/features/specialOffersContainer/types';
import { IOfferAdvertising } from '~/src/features/specialOffersContainer/types';
import { TState } from '~/src/store';

import { CustomArrows, CustomArrowsWithShowAll } from './CustomArrows';

import * as ST from './styled';

interface IProps {
    isLoading: boolean,
    offers: TSpecOffer[],
    showAllButton?: boolean,
    root?: TSegmentedBannerAlias,
}

export const LkPromotionalCardsCarousel = ({ isLoading, offers, showAllButton, root }: IProps) => {
    const { specOffers: { isPopupOpened, popupState }, popupOffer } = useSelector((state: TState) => ({
        specOffers: state.specOffers,
        popupOffer: selectOffer(state),
    }), shallowEqual);

    const dispatch = useDispatch();

    const callbackMap = useCallbackMap(root);

    const isMobile = useMatchMediaByWidth(wideBreakpoints.mobile);

    const [labelId, setLabelId] = useState<TSpecOffer['id'] | null>(null);

    useEffect(() => {
        if (!offers.length) {
            return;
        }

        const titles: ISpecOffer['title'][] = [];
        const types: string[] = [];

        offers.forEach(offer => {
            titles.push(offer.title);
            types.push(getGaLabelByNavigationType(offer).replace(' | type:', ''));
        });

        specialOfferAnalytics.viewOffersList(`list:[name:${titles.join(', ')}; type:${types.join()}]`);
    }, []);

    const onClosePopup = () => {
        batch(() => {
            dispatch(setPopupState('none'));
            dispatch(setIsPopupOpened(false));
        });
    };

    const offerCards = useMemo(() => {
        return offers.map(offer => {
            const { id, advertising } = offer;

            const offerAdvertising: IOfferAdvertising | undefined = advertising
                ? {
                    ...advertising,
                    isShown: labelId === id,
                    onClick: () => setLabelId(id),
                    onClose: () => setLabelId(null),
                }
                : undefined;

            return (
                <LkPromotionalCard
                    key={id}
                    offer={{ ...offer, advertising: offerAdvertising }}
                    callbackMap={callbackMap}
                />
            );
        });
    }, [offers, labelId, callbackMap]);

    const isNewOfferDetails = useMemo(
        () => popupOffer?.isShowNewDetail && isNewDetailsDesign(),
        [popupOffer?.isShowNewDetail],
    );

    const promoPopupProps = {
        isPopupOpen: isPopupOpened,
        offer: popupOffer,
        callbackMap: callbackMap,
        onClose: onClosePopup,
    };

    return (
        <ST.SwiperContainer hideControls={offerCards.length === 1}>
            <ST.CustomSwiper
                isLoading={isLoading}
                slideWidth={`${isMobile ? '343' : '352'}px`}
                customArrow={showAllButton ? CustomArrowsWithShowAll : CustomArrows}
                cellSpacing={32}
                onDragStart={() => setLabelId(null)}
                data-test='lkPromotionalCardsCarousel-root'
            >
                {offerCards}
            </ST.CustomSwiper>

            {/* Use LkPromotionalPopup out from LkPromotionalCard because
                controls do not work well inside NukaCarousel */}
            {isNewOfferDetails
                ? <SpecOfferDetails {...promoPopupProps}/>
                : <LkPromotionalPopup {...promoPopupProps}/>
            }

            <PromoConnectedPopup
                isOpen={popupState === 'connected'}
                offer={popupOffer || ({} as ISpecOffer)}
                onClose={onClosePopup}
                data-test='promoConnectedPopup_root'
            />
            <PromoConnectionPopup
                isPopupOpen={popupState === 'needConfirmation'}
                offer={popupOffer}
                callbackMap={callbackMap}
                onClose={onClosePopup}
            />
            <PromoErrorPopup
                isOpen={popupState === 'connectionError'}
                onClose={onClosePopup}
                data-test='promoErrorPopup_root'
            />
            <PromoNeedPaymentPopup
                isOpen={popupState === 'needPayment'}
                offer={popupOffer || ({} as ISpecOffer)}
                onClose={onClosePopup}
            />
            <PromoNeedSetPhonePopup
                isOpen={popupState === 'needSetPhone'}
                offer={popupOffer || ({} as TSpecOffer)}
                onActivate={callbackMap[DefaultCallback]}
                onClose={onClosePopup}
            />
        </ST.SwiperContainer>
    );
};

export default LkPromotionalCardsCarousel;
